import { useEffect, useState } from 'react';
import { useDashboardContext } from './Context';
import { useAuthContext } from '../../contexts/AuthContext';
import { DashboardService } from './service';
import KpiCard from './KpiCard';

const service = new DashboardService();

const formatPercent = (value: number) => `${value.toFixed(1)}%`;
const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function ClientsDashboard() {
    const { startAt, endAt, atendenteId } = useDashboardContext();
    const { user } = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const params: any = { startAt, endAt };
                if (user?.admin || user?.root) {
                    if (atendenteId) params.attendantId = atendenteId;
                }
                const response = await service.getClientes(params);
                setData(response.data);
            } catch (error) {
                console.error('Erro ao carregar dashboard de clientes', error);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [startAt, endAt, atendenteId, user?.admin, user?.root]);

    if (!data) {
        return (
            <div className="card sf-dashboard-panel">
                <div className="card-body">{loading ? 'Carregando...' : 'Sem dados'}</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <KpiCard title="Clientes" value={data.summary.totalClients} tone="primary" />
                <KpiCard title="Novos" value={data.summary.newClients} tone="success" />
                <KpiCard title="Recorrentes" value={data.summary.recurringClients} tone="info" />
                <KpiCard title="Rebooking" value={formatPercent(data.rebooking.rate)} tone="secondary" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Retenção</h3>
                        <div className="mt-3 space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                                <span>30 dias</span>
                                <span className="font-medium">{data.retention.r30}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>60 dias</span>
                                <span className="font-medium">{data.retention.r60}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>90 dias</span>
                                <span className="font-medium">{data.retention.r90}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card sf-dashboard-panel xl:col-span-2">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Top clientes</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs uppercase text-base-content/60">Por receita</div>
                                <ul className="mt-2 space-y-2">
                                    {(data.topClientsByRevenue || []).map((client: any) => (
                                        <li key={client.clientId} className="flex items-center justify-between">
                                            <span className="text-sm">{client.name}</span>
                                            <span className="text-sm font-medium">
                                                {formatCurrency(client.totalRevenue || 0)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="text-xs uppercase text-base-content/60">Por visitas</div>
                                <ul className="mt-2 space-y-2">
                                    {(data.topClientsByVisits || []).map((client: any) => (
                                        <li key={client.clientId} className="flex items-center justify-between">
                                            <span className="text-sm">{client.name}</span>
                                            <span className="text-sm font-medium">{client.totalVisits} visitas</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
